/**
 * **Única vista de sesión de llamada** (timbrando / en conversación) para WebRTC del call center.
 * Manual (`call-center-start-call` + FAB) y automática (Realtime / evento) comparten exactamente
 * este componente: mismo teléfono, mismo resultado visual, una sola ruta de mantenimiento.
 *
 * El marcador en reposo (teclado) vive en `FloatingDialerWidget`; al pasar a `callPhase !== 'none'`
 * se muestra esto — no hay una segunda implementación paralela.
 */

import { useCallback, useEffect, useState } from 'react';
import { Box, Button, InlineStack, Text, TextField, Popover } from '@/lib/polaris';
import { PersonFilledIcon } from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';
import { phonesMatch } from '@/lib/crmPhoneMatch';
import { formatSecondsAsClock } from '@/lib/formatClockDuration';
import { isPstnAnsweredForUi } from '@/lib/callCenterPstnConversation';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';
import { CallCenterActiveCallRightColumn } from '@/components/callCenter/CallCenterActiveCallRightColumn';
import {
  FAB_DIALER_LEFT_FLEX,
  FAB_DIALER_LEFT_MAX_WIDTH,
  FAB_DIALER_RIGHT_FLEX,
} from '@/config/callCenterFabDialerLayout';

export type CallCenterPhoneActiveSessionSurface = 'fab' | 'corner';

export interface CallCenterPhoneActiveSessionProps {
  /**
   * `fab`: panel del marcador flotante (separador vertical, avatar grande).
   * `corner`: tarjeta fija cuando no hay FAB CRM; avatar compacto, sin separador.
   */
  surface: CallCenterPhoneActiveSessionSurface;
  tenantId: string;
  crmCallId?: string | null;
  /** `crm_calls.call_status` vía store/webhook; hasta `answered` se muestra timbrado, no “En llamada”. */
  crmCallStatus?: string | null;
  remotePhone: string | null;
  callPhase: WebrtcCallUiPhase;
  callDirection: 'inbound' | 'outbound' | null;
  elapsedSeconds: number;
  onHangup: () => void;
  onOpenDialer: () => void;
  hangingUp: boolean;
  isMuted?: boolean;
  isOnHold?: boolean;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
  onTransfer?: (transferTo: string) => Promise<{ success: boolean; message?: string }>;
  onWarmTransfer?: (action: 'initiate' | 'complete' | 'cancel', transferTo?: string) => Promise<{ success: boolean; message?: string }>;
  warmTransferActive?: boolean;
  isRecordingPaused?: boolean;
  onToggleRecordingPause?: () => void;
  /**
   * Ancho en px de la columna del teclado medido en reposo (FAB). Si falta, el layout % se aplica sobre un ancho mal calculado.
   */
  fabLeftColumnWidthPx?: number | null;
}

interface LeadMatch {
  id: string;
  name: string | null;
  phone: string | null;
}

function avatarInitialsFromName(name: string | null | undefined): string | null {
  const n = name?.trim();
  if (!n) return null;
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[parts.length - 1][0];
    if (a && b) return (a + b).toUpperCase();
  }
  return n.slice(0, 2).toUpperCase();
}

function ActiveSessionAvatarColumn({
  tenantId,
  remotePhone,
  elapsedSeconds,
  onHangup,
  hangingUp,
  isFabLayout,
  uiRinging,
  uiInConversation,
  isMuted = false,
  isOnHold = false,
  onToggleMute,
  onToggleHold,
  onTransfer,
  onWarmTransfer,
  warmTransferActive = false,
  isRecordingPaused = false,
  onToggleRecordingPause,
  fabLeftColumnWidthPx = null,
}: {
  tenantId: string;
  remotePhone: string | null;
  elapsedSeconds: number;
  onHangup: () => void;
  hangingUp: boolean;
  isFabLayout: boolean;
  uiRinging: boolean;
  uiInConversation: boolean;
  isMuted?: boolean;
  isOnHold?: boolean;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
  onTransfer?: (transferTo: string) => Promise<{ success: boolean; message?: string }>;
  onWarmTransfer?: (action: 'initiate' | 'complete' | 'cancel', transferTo?: string) => Promise<{ success: boolean; message?: string }>;
  warmTransferActive?: boolean;
  isRecordingPaused?: boolean;
  onToggleRecordingPause?: () => void;
  fabLeftColumnWidthPx?: number | null;
}) {
  const [leadName, setLeadName] = useState<string | null>(null);
  const [transferPopoverActive, setTransferPopoverActive] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferMode, setTransferMode] = useState<'choose' | 'blind' | 'warm'>('choose');

  const handleTransferSubmit = useCallback(async () => {
    if (!transferNumber.trim()) return;
    setTransferring(true);
    if (transferMode === 'warm' && onWarmTransfer) {
      const result = await onWarmTransfer('initiate', transferNumber.trim());
      setTransferring(false);
      if (result.success) {
        setTransferPopoverActive(false);
        setTransferNumber('');
        setTransferMode('choose');
      }
    } else if (onTransfer) {
      const result = await onTransfer(transferNumber.trim());
      setTransferring(false);
      if (result.success) {
        setTransferPopoverActive(false);
        setTransferNumber('');
        setTransferMode('choose');
      }
    } else {
      setTransferring(false);
    }
  }, [onTransfer, onWarmTransfer, transferNumber, transferMode]);

  useEffect(() => {
    const raw = remotePhone?.trim();
    if (!tenantId || !raw) {
      setLeadName(null);
      return;
    }
    let cancelled = false;
    const last9 = raw.replace(/\D/g, '').slice(-9);
    (async () => {
      const { data, error } = await supabase
        .from('crm_leads')
        .select('id, name, phone')
        .eq('tenant_id', tenantId)
        .ilike('phone', `%${last9}%`)
        .limit(15);
      if (cancelled || error || !data?.length) {
        if (!cancelled) setLeadName(null);
        return;
      }
      const rows = data as LeadMatch[];
      const match = rows.find((r) => phonesMatch(r.phone, raw)) ?? null;
      if (!cancelled) setLeadName(match?.name?.trim() ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, remotePhone]);

  const initials = avatarInitialsFromName(leadName);
  const avatarPx = isFabLayout ? 96 : 56;
  const personIconPx = Math.round(avatarPx * 0.78);
  const timerLabel = formatSecondsAsClock(elapsedSeconds);

  const useFabFixedLeft =
    isFabLayout && fabLeftColumnWidthPx != null && fabLeftColumnWidthPx > 0;
  const fabLeftPx = useFabFixedLeft ? Math.round(fabLeftColumnWidthPx) : 0;

  return (
    <div
      style={{
        /**
         * FAB: si hay ancho medido del teclado, `flex-basis` en px = misma columna que en reposo.
         * Si solo hay %, el padre debe ocupar el 100% del panel (ver raíz de la sesión).
         */
        ...(useFabFixedLeft
          ? {
              flex: `0 0 ${fabLeftPx}px`,
              maxWidth: fabLeftPx,
              minWidth: 0,
            }
          : {
              flex: isFabLayout ? FAB_DIALER_LEFT_FLEX : '0 0 36%',
              minWidth: isFabLayout ? 0 : 100,
              maxWidth: isFabLayout ? FAB_DIALER_LEFT_MAX_WIDTH : undefined,
            }),
        background: 'var(--p-color-bg-fill-inverse)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isFabLayout ? 'var(--p-space-500)' : 'var(--p-space-400)',
        paddingTop: uiRinging
          ? isFabLayout
            ? 'var(--p-space-1000)'
            : 'var(--p-space-800)'
          : undefined,
        paddingInline: uiRinging
          ? isFabLayout
            ? 'var(--p-space-600)'
            : 'var(--p-space-500)'
          : undefined,
        boxSizing: 'border-box',
        gap: isFabLayout ? 'var(--p-space-400)' : undefined,
        /** La altura la fija el panel del FAB (`height` en el contenedor); aquí solo rellenamos. */
        minHeight: isFabLayout ? 0 : undefined,
        alignSelf: isFabLayout ? 'stretch' : undefined,
      }}
    >
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Text as="p" variant="bodySm" tone="text-inverse">
          {uiInConversation ? `En llamada · ${timerLabel}` : 'Timbrando'}
        </Text>
        {uiRinging ? (
          <span className="sf-call-status-dots sf-call-status-dots--inverse" aria-hidden>
            <span className="sf-call-status-dot" />
            <span className="sf-call-status-dot" />
            <span className="sf-call-status-dot" />
          </span>
        ) : null}
      </InlineStack>

      <div
        className={uiRinging ? 'sf-call-ring-wrap sf-call-ring-wrap--ringing' : 'sf-call-ring-wrap'}
        style={{
          width: avatarPx,
          height: avatarPx,
          marginTop: uiRinging ? (isFabLayout ? 10 : 6) : 0,
        }}
      >
        <div className="sf-call-ring-ripple" aria-hidden />
        <div
          className={
            isFabLayout ? 'sf-call-ring-avatar sf-call-ring-avatar--embedded' : 'sf-call-ring-avatar'
          }
        >
          {initials ? (
            <span
              style={{
                fontSize: isFabLayout ? '1.75rem' : '1.1rem',
                fontWeight: 700,
                lineHeight: 1,
                color: 'var(--p-color-text)',
                letterSpacing: '-0.02em',
                userSelect: 'none',
              }}
              aria-hidden
            >
              {initials}
            </span>
          ) : (
            <PersonFilledIcon
              width={personIconPx}
              height={personIconPx}
              style={{ color: 'var(--p-color-text)', display: 'block', flexShrink: 0 }}
              aria-hidden
            />
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p-space-200)', width: isFabLayout ? '100%' : undefined }}>
        {uiInConversation && (onToggleMute || onToggleHold) ? (
          <InlineStack gap="200" align="center" wrap={false}>
            {onToggleMute ? (
              <Button
                variant={isMuted ? 'primary' : 'secondary'}
                size="slim"
                onClick={onToggleMute}
                accessibilityLabel={isMuted ? 'Activar micrófono' : 'Silenciar'}
              >
                {isMuted ? '🔇 Muted' : '🎤 Mute'}
              </Button>
            ) : null}
            {onToggleHold ? (
              <Button
                variant={isOnHold ? 'primary' : 'secondary'}
                size="slim"
                onClick={onToggleHold}
                accessibilityLabel={isOnHold ? 'Reanudar llamada' : 'Pausar llamada'}
              >
                {isOnHold ? '▶ Reanudar' : '⏸ Hold'}
              </Button>
            ) : null}
            {onToggleRecordingPause ? (
              <Button
                variant={isRecordingPaused ? 'primary' : 'secondary'}
                size="slim"
                tone={isRecordingPaused ? 'critical' : undefined}
                onClick={onToggleRecordingPause}
                accessibilityLabel={isRecordingPaused ? 'Reanudar grabación' : 'Pausar grabación'}
              >
                {isRecordingPaused ? '⏺ Grabar' : '⏸ Rec'}
              </Button>
            ) : null}
          </InlineStack>
        ) : null}
        {uiInConversation && (onTransfer || onWarmTransfer) && !warmTransferActive ? (
          <Popover
            active={transferPopoverActive}
            activator={
              <Button
                variant="secondary"
                size="slim"
                onClick={() => { setTransferPopoverActive((v) => !v); setTransferMode('choose'); }}
                accessibilityLabel="Transferir llamada"
              >
                Transferir
              </Button>
            }
            onClose={() => { setTransferPopoverActive(false); setTransferMode('choose'); }}
            preferredAlignment="center"
          >
            <Box padding="300">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p-space-200)', minWidth: 220 }}>
                {transferMode === 'choose' ? (
                  <>
                    {onTransfer ? (
                      <Button variant="secondary" size="slim" onClick={() => setTransferMode('blind')}>
                        Transferencia directa
                      </Button>
                    ) : null}
                    {onWarmTransfer ? (
                      <Button variant="secondary" size="slim" onClick={() => setTransferMode('warm')}>
                        Transferencia asistida
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <TextField
                      label={transferMode === 'warm' ? 'Consultar con' : 'Transferir a'}
                      value={transferNumber}
                      onChange={setTransferNumber}
                      placeholder="Número o extensión"
                      autoComplete="off"
                      autoFocus
                    />
                    <Button
                      variant="primary"
                      size="slim"
                      onClick={handleTransferSubmit}
                      loading={transferring}
                      disabled={!transferNumber.trim()}
                    >
                      {transferMode === 'warm' ? 'Llamar destino' : 'Transferir'}
                    </Button>
                    <Button variant="tertiary" size="slim" onClick={() => setTransferMode('choose')}>
                      Volver
                    </Button>
                  </>
                )}
              </div>
            </Box>
          </Popover>
        ) : null}
        {uiInConversation && warmTransferActive && onWarmTransfer ? (
          <InlineStack gap="200" align="center" wrap={false}>
            <Button
              variant="primary"
              size="slim"
              onClick={async () => { setTransferring(true); await onWarmTransfer('complete'); setTransferring(false); }}
              loading={transferring}
            >
              Completar
            </Button>
            <Button
              tone="critical"
              size="slim"
              onClick={async () => { setTransferring(true); await onWarmTransfer('cancel'); setTransferring(false); }}
              loading={transferring}
            >
              Cancelar
            </Button>
          </InlineStack>
        ) : null}
        <Button
          tone="critical"
          variant="primary"
          size="large"
          fullWidth={isFabLayout}
          onClick={() => onHangup()}
          loading={hangingUp}
          accessibilityLabel="Colgar"
        >
          Colgar
        </Button>
      </div>
    </div>
  );
}

export function CallCenterPhoneActiveSession({
  surface,
  tenantId,
  crmCallId = null,
  crmCallStatus = null,
  remotePhone,
  callPhase,
  callDirection,
  elapsedSeconds,
  onHangup,
  onOpenDialer,
  hangingUp,
  isMuted = false,
  isOnHold = false,
  onToggleMute,
  onToggleHold,
  onTransfer,
  onWarmTransfer,
  warmTransferActive = false,
  isRecordingPaused = false,
  onToggleRecordingPause,
  fabLeftColumnWidthPx = null,
}: CallCenterPhoneActiveSessionProps) {
  const isFab = surface === 'fab';
  const rightVariant = isFab ? 'fab' : 'floating';
  const fabLeftMeasured = fabLeftColumnWidthPx != null && fabLeftColumnWidthPx > 0;

  const pstnAnswered = isPstnAnsweredForUi(crmCallId, crmCallStatus);
  const uiRinging =
    callPhase === 'ringing' || (callPhase === 'active' && !pstnAnswered);
  const uiInConversation = callPhase === 'active' && pstnAnswered;

  const rightPanel = (
    <CallCenterActiveCallRightColumn
      variant={rightVariant}
      tenantId={tenantId}
      crmCallId={crmCallId}
      crmCallStatus={crmCallStatus}
      remotePhone={remotePhone}
      callPhase={callPhase}
      callDirection={callDirection}
      elapsedSeconds={elapsedSeconds}
      onOpenDialer={onOpenDialer}
    />
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        boxSizing: 'border-box',
        ...(isFab
          ? {
              /** Único hijo de la fila del panel: sin `flex:1`/`width:100%` el ítem se encoge al min-content y el 46% interno es ~35% del modal. */
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              flex: '1 1 auto',
              height: '100%',
              minHeight: 0,
            }
          : { minHeight: 120 }),
      }}
    >
      {/**
       * Audio del destino (timbrado remoto, buzón “después del tono…”, etc.) sigue por el mismo WebRTC
       * (`telnyx-remote-audio`); unificar UI no corta ese flujo.
       */}
      <ActiveSessionAvatarColumn
        tenantId={tenantId}
        remotePhone={remotePhone}
        elapsedSeconds={elapsedSeconds}
        onHangup={onHangup}
        hangingUp={hangingUp}
        isFabLayout={isFab}
        uiRinging={uiRinging}
        uiInConversation={uiInConversation}
        isMuted={isMuted}
        isOnHold={isOnHold}
        onToggleMute={onToggleMute}
        onToggleHold={onToggleHold}
        onTransfer={onTransfer}
        onWarmTransfer={onWarmTransfer}
        warmTransferActive={warmTransferActive}
        isRecordingPaused={isRecordingPaused}
        onToggleRecordingPause={onToggleRecordingPause}
        fabLeftColumnWidthPx={fabLeftColumnWidthPx}
      />
      {isFab ? <Box borderInlineStartWidth="025" borderColor="border" /> : null}
      {isFab ? (
        <Box
          minWidth={0}
          minHeight={0}
          style={{
            flex: fabLeftMeasured ? '1 1 0%' : FAB_DIALER_RIGHT_FLEX,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{rightPanel}</div>
        </Box>
      ) : (
        rightPanel
      )}
    </div>
  );
}
